// File: frontend/src/views/private/Account.js

import React, { useEffect, useState, useRef } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { useApi } from '../../hooks/useApi';
import PageHeader from '../../components/layout/PageHeader';
import PageContainer from '../../components/layout/PageContainer';
import LoadingSpinner from '../../components/utility/LoadingSpinner';
import RecentActivities from '../../components/dashboard/RecentActivities';
import { 
  User, 
  Mail, 
  Phone, 
  Shield, 
  Globe, 
  Calendar,
  CheckCircle,
  AlertCircle,
  Edit2,
  Save,
  X
} from 'lucide-react';
import styles from './Account.module.css';

const Account = () => {
  const { user, updateUser } = useAuth();
  const { execute, isLoading } = useApi();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editedProfile, setEditedProfile] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  
  // Prevent duplicate calls in React 18 Strict Mode
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    // Only load once, even in Strict Mode
    if (!hasLoadedRef.current && user?.user_id) {
      hasLoadedRef.current = true;
      loadProfile();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  const loadProfile = async () => {
    if (!user?.user_id) return;

    try {
      const response = await execute('getProfile', {}, user.user_id, true);
      
      if (response.success) {
        setProfile(response.data.profile);
        setEditedProfile({
          first_name: response.data.profile.first_name || '',
          last_name: response.data.profile.last_name || '',
          middle_name: response.data.profile.middle_name || '',
          prefix: response.data.profile.prefix || '',
          suffix: response.data.profile.suffix || '',
          date_of_birth: response.data.profile.date_of_birth || '',
          gender: response.data.profile.gender || '',
          marital_status: response.data.profile.marital_status || '',
          primary_language: response.data.profile.primary_language || 'en-us',
          country_of_residence: response.data.profile.country_of_residence || ''
        });
      } else {
        setError(response.error || 'Failed to load profile');
      }
    } catch (err) {
      setError(err.message || 'Failed to load profile');
    }
  };

  const handleEdit = () => {
    setIsEditing(true);
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setSaveError(null);
    setSaveSuccess(false);
    // Reset to original values
    setEditedProfile({
      first_name: profile.first_name || '',
      last_name: profile.last_name || '',
      middle_name: profile.middle_name || '',
      prefix: profile.prefix || '',
      suffix: profile.suffix || '',
      date_of_birth: profile.date_of_birth || '',
      gender: profile.gender || '',
      marital_status: profile.marital_status || '',
      primary_language: profile.primary_language || 'en-us',
      country_of_residence: profile.country_of_residence || ''
    });
  };

  const handleSave = async () => {
    setSaveError(null);
    setSaveSuccess(false);

    try {
      const response = await execute('updateProfile', editedProfile, user.user_id, true);
      
      if (response.success) {
        setProfile(response.data.profile);
        setIsEditing(false);
        setSaveSuccess(true);
        
        // Update auth context
        updateUser({
          first_name: response.data.profile.first_name,
          last_name: response.data.profile.last_name
        });

        setTimeout(() => setSaveSuccess(false), 3000);
      } else {
        setSaveError(response.error || 'Failed to update profile');
      }
    } catch (err) {
      setSaveError(err.message || 'Failed to update profile');
    }
  };

  const handleInputChange = (field, value) => {
    setEditedProfile(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatPhone = (phone) => {
    if (!phone) return 'Not provided';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 11 && cleaned.startsWith('1')) {
      return `(${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
    }
    return phone;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not provided';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return 'Not provided';
    }
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return 'Not provided';
    const [year, month, day] = dateString.split('-');
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
                        'July', 'August', 'September', 'October', 'November', 'December'];
    return `${monthNames[parseInt(month) - 1]} ${parseInt(day)}, ${year}`;
  };

  if (isLoading && !profile) {
    return (
      <PageContainer>
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
        </div>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <div className={styles.errorContainer}>
          <div className={styles.errorCard}>
            <AlertCircle size={48} className={styles.errorIcon} />
            <h2>Error Loading Profile</h2>
            <p>{error}</p>
            <button onClick={loadProfile} className={styles.retryButton}>
              Try Again
            </button>
          </div>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageHeader 
        title="Account Settings"
        subtitle="Manage your personal information and preferences"
        showBack={true}
        backTo="/dashboard"
        actions={
          !isEditing ? (
            <button onClick={handleEdit} className={styles.editButton}>
              <Edit2 size={20} strokeWidth={2} />
              Edit Profile
            </button>
          ) : (
            <div className={styles.editActions}>
              <button onClick={handleCancel} className={styles.cancelButton}>
                <X size={20} strokeWidth={2} />
                Cancel
              </button>
              <button onClick={handleSave} className={styles.saveButton} disabled={isLoading}>
                <Save size={20} strokeWidth={2} />
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )
        }
      />

      {saveSuccess && (
        <div className={styles.successBanner}>
          <CheckCircle size={20} strokeWidth={2} />
          Profile updated successfully!
        </div>
      )}

      {saveError && (
        <div className={styles.errorBanner}>
          <AlertCircle size={20} strokeWidth={2} />
          {saveError}
        </div>
      )}

      <div className={styles.sections}>
        {/* Personal Information */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <User size={20} strokeWidth={2} />
            <h2>Personal Information</h2>
          </div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Prefix</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.prefix}
                  onChange={(e) => handleInputChange('prefix', e.target.value)}
                  className={styles.input}
                  placeholder="Mr., Mrs., Dr., etc."
                />
              ) : (
                <span className={styles.value}>{profile?.prefix || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>First Name</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.first_name}
                  onChange={(e) => handleInputChange('first_name', e.target.value)}
                  className={styles.input}
                  required
                />
              ) : (
                <span className={styles.value}>{profile?.first_name || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Middle Name</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.middle_name}
                  onChange={(e) => handleInputChange('middle_name', e.target.value)}
                  className={styles.input}
                  placeholder="Optional"
                />
              ) : (
                <span className={styles.value}>{profile?.middle_name || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Last Name</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.last_name}
                  onChange={(e) => handleInputChange('last_name', e.target.value)}
                  className={styles.input}
                  required
                />
              ) : (
                <span className={styles.value}>{profile?.last_name || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Suffix</span>
              {isEditing ? (
                <input
                  type="text"
                  value={editedProfile.suffix}
                  onChange={(e) => handleInputChange('suffix', e.target.value)}
                  className={styles.input}
                  placeholder="Jr., Sr., III, etc."
                />
              ) : (
                <span className={styles.value}>{profile?.suffix || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Date of Birth</span>
              {isEditing ? (
                <input
                  type="date"
                  value={editedProfile.date_of_birth}
                  onChange={(e) => handleInputChange('date_of_birth', e.target.value)}
                  className={styles.input}
                />
              ) : (
                <span className={styles.value}>{formatDateOnly(profile?.date_of_birth)}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Gender</span>
              {isEditing ? (
                <select
                  value={editedProfile.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="prefer-not-to-say">Prefer not to say</option>
                </select>
              ) : (
                <span className={styles.value}>{profile?.gender || 'Not provided'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Marital Status</span>
              {isEditing ? (
                <select
                  value={editedProfile.marital_status}
                  onChange={(e) => handleInputChange('marital_status', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select...</option>
                  <option value="single">Single</option>
                  <option value="married">Married</option>
                  <option value="divorced">Divorced</option>
                  <option value="widowed">Widowed</option>
                  <option value="separated">Separated</option>
                </select>
              ) : (
                <span className={styles.value}>{profile?.marital_status || 'Not provided'}</span>
              )}
            </div>
          </div>
        </section>

        {/* Contact Information */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Mail size={20} strokeWidth={2} />
            <h2>Contact Information</h2>
          </div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Email Address</span>
              <div className={styles.valueWithBadge}>
                <span className={styles.value}>{profile?.email_address || 'Not provided'}</span>
                <span className={styles.lockBadge}>🔒 Protected</span>
              </div>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Email Verified</span>
              <span className={`${styles.badge} ${profile?.is_email_verified ? styles.badgeSuccess : styles.badgeWarning}`}>
                {profile?.is_email_verified ? (
                  <><CheckCircle size={14} strokeWidth={2} /> Verified</>
                ) : (
                  <><AlertCircle size={14} strokeWidth={2} /> Pending</>
                )}
              </span>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Phone Number</span>
              <div className={styles.valueWithBadge}>
                <span className={styles.value}>{formatPhone(profile?.phone_number)}</span>
                <span className={styles.lockBadge}>🔒 Protected</span>
              </div>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Phone Verified</span>
              <span className={`${styles.badge} ${profile?.is_phone_verified ? styles.badgeSuccess : styles.badgeWarning}`}>
                {profile?.is_phone_verified ? (
                  <><CheckCircle size={14} strokeWidth={2} /> Verified</>
                ) : (
                  <><AlertCircle size={14} strokeWidth={2} /> Pending</>
                )}
              </span>
            </div>
          </div>
        </section>

        {/* Account Status */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Shield size={20} strokeWidth={2} />
            <h2>Account Status</h2>
          </div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Status</span>
              <span className={`${styles.badge} ${styles.badgeSuccess}`}>
                <CheckCircle size={14} strokeWidth={2} />
                {profile?.user_status || 'Active'}
              </span>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Role</span>
              <span className={styles.value}>{profile?.user_role || 'Standard'}</span>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Member Since</span>
              <span className={styles.value}>{formatDate(profile?.created_at)}</span>
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Last Login</span>
              <span className={styles.value}>{formatDate(profile?.last_login_at)}</span>
            </div>
          </div>
        </section>

        {/* Preferences */}
        <section className={styles.section}>
          <div className={styles.sectionHeader}>
            <Globe size={20} strokeWidth={2} />
            <h2>Preferences</h2>
          </div>
          <div className={styles.card}>
            <div className={styles.row}>
              <span className={styles.label}>Primary Language</span>
              {isEditing ? (
                <select
                  value={editedProfile.primary_language}
                  onChange={(e) => handleInputChange('primary_language', e.target.value)}
                  className={styles.select}
                >
                  <option value="en-us">English (US)</option>
                  <option value="en-ca">English (CA)</option>
                  <option value="es-mx">Español (MX)</option>
                  <option value="fr-ca">Français (CA)</option>
                </select>
              ) : (
                <span className={styles.value}>{profile?.primary_language || 'en-us'}</span>
              )}
            </div>

            <div className={styles.row}>
              <span className={styles.label}>Country of Residence</span>
              {isEditing ? (
                <select
                  value={editedProfile.country_of_residence}
                  onChange={(e) => handleInputChange('country_of_residence', e.target.value)}
                  className={styles.select}
                >
                  <option value="">Select...</option>
                  <option value="US">United States</option>
                  <option value="CA">Canada</option>
                  <option value="MX">Mexico</option>
                </select>
              ) : (
                <span className={styles.value}>{profile?.country_of_residence || 'Not provided'}</span>
              )}
            </div>
          </div>
        </section>

        {/* ✅ ADD THIS: Recent Activities Section */}
        <RecentActivities limit={10} showViewAll={false} />
      </div>
    </PageContainer>
  );
};

export default Account;